import { useContext, useEffect, useState } from 'react';
import AccountContext from '../../services/AccountContext';
import getConfig from 'next/config';
import { useRouter } from 'next/router';

// TODO: store in config somewhere?
const key = 'UA-5641939-1';

// Only track when in production
const {
  publicRuntimeConfig: { APP_ENV },
} = getConfig();
const loadTrackingScripts = APP_ENV === 'prod';

// Only load the snippet once per page load
let isLoaded = false;
const load = () => {
  // Don't load during SSR
  if (typeof window === 'undefined') {
    return;
  }

  if (isLoaded) {
    return;
  }
  isLoaded = true;

  // Mock analytics methods in QA/dev
  if (!loadTrackingScripts) {
    window.ga = function() {
      const args = [].slice.call(arguments);
      if (args[0] && args[0].substr(0, 3) === 'gtm') {
        return;
      }
      console.log('ga', args);
    };
  } else {
    // Load real tracking script
    (function(i, s, o, g, r, a, m) {
      i['GoogleAnalyticsObject'] = r;
      (i[r] =
        i[r] ||
        function() {
          (i[r].q = i[r].q || []).push(arguments);
        }),
        (i[r].l = 1 * new Date());
      (a = s.createElement(o)), (m = s.getElementsByTagName(o)[0]);
      a.async = 1;
      a.src = g;
      m.parentNode.insertBefore(a, m);
    })(
      window,
      document,
      'script',
      'https://www.google-analytics.com/analytics.js',
      'ga'
    );

    window.ga('create', key, {});
  }
};

export function getDimensions(
  memberId = null,
  memberType = null,
  isPremium = null,
  grades = [],
  subjects = [],
  contentType = null,
  pageType = null,
  isInteractive = null
) {
  /*
   DIMENSIONS MAPPING:
     dimension3: Member Type
     dimension4: isPremium?
     dimension6: Grades
     contentGroup1: Grades
     dimension15: Top-level subjects
     dimension16: Content Type
     dimension17: Page Type (Search or Content)
     user_id: Member ID
     isInteractive: isInteractive? (i.e. is this worksheet interactive?)
   */

  const data = {};
  const emptyField = 'none';

  if (memberId) {
    data.dimension3 = memberType;
    data.dimension4 = isPremium ? 'pro' : 'basic';
  } else {
    data.dimension3 = data.dimension4 = data.user_id = emptyField;
  }

  if (memberId && memberType && memberType !== 'student') {
    data.user_id = memberId;
  }

  if (grades?.length) {
    data.dimension6 = grades.join(', ');
    data.contentGroup1 = grades.join(', ');
  } else {
    data.dimension6 = emptyField;
  }

  const rootResults = [];
  if (subjects?.length) {
    // Get the relevant root subjects (one of the ones defined below).
    // Works for URLs (Search page) and internalNames (Content pages).
    const subjectMapping = {
      ela: 'Reading & writing',
      math: 'Math',
      typing: 'Typing',
      'activity.topic.science': 'Science',
      science: 'Science',
      'activity.topic.socialstudies': 'Social studies',
      'social-studies': 'Social studies',
    };
    for (const subj of subjects) {
      for (const root of Object.keys(subjectMapping)) {
        if (typeof subj === 'string' && subj.startsWith(root)) {
          rootResults.push(subjectMapping[root]);
          delete subjectMapping[root];
          break;
        }
      }
    }
  }
  data.dimension15 =
    rootResults?.length !== 0 ? rootResults.join(', ') : emptyField;

  data.dimension16 = contentType ? contentType : emptyField;
  data.dimension17 = pageType ? pageType : emptyField;

  if (isInteractive !== null) {
    data.isInteractive = isInteractive;
  }

  return data;
}

export default function useGoogleAnalytics(
  grades = [],
  subjects = [],
  contentType = null,
  pageType = null,
  isInteractive = null
) {
  load();
  const { ready, memberId, memberType, isPremium } = useContext(AccountContext);

  // pageTracked keeps track of the path of the last visited page so we can detect pageviews
  const [pageTracked, setPageTracked] = useState(null);

  const router = useRouter();

  // Track member data and page view
  useEffect(() => {
    // Skip if auth isn't ready yet
    if (!ready) {
      return;
    }

    // Track page view
    if (pageTracked !== router.asPath) {
      setPageTracked(router.asPath);

      // document.title was not updating consistently because of a NextJS issue. Accessing document.title inside a
      // timeout in routeChangeComplete is a workaround. For more info: https://github.com/vercel/next.js/issues/6025
      // This issue should already be resolved in next@9.4.3
      setTimeout(() => {
        window.ga(
          'set',
          getDimensions(
            memberId,
            memberType,
            isPremium,
            grades,
            subjects,
            contentType,
            pageType,
            isInteractive
          )
        );
        window.ga('set', 'page', router.asPath);
        window.ga('set', 'title', document.title);
        window.ga('send', 'pageview');
      }, 0);
    }
  }, [ready, memberId, router.asPath]);
}
